import { Typography } from '@design-system/components/typography/typography';
import { Toggle } from '@client/components/common/inputs/Toggle';
import { Tooltip } from '@design-system/components/tooltip/tooltip';
import { useCollectionSwapConfigQuery } from 'generated/graphql';
import { useIsCreatorApprovedSwapsEnabled } from '../model/useIsCreatorApprovedSwapsEnabled';
import * as styles from './CreatorApprovedSwaps.css';

export const WorkoutSwapsToggle = ({
  value,
  onChange,
  influencerId,
  programId,
  disabled,
}: {
  value: boolean | undefined;
  onChange: (value: boolean) => void;
  influencerId: number;
  programId?: number;
  disabled?: boolean;
}) => {
  const enabled = useIsCreatorApprovedSwapsEnabled(influencerId);
  const { data } = useCollectionSwapConfigQuery({
    variables: { collectionId: programId ?? 0 },
    skip: !enabled || programId === undefined,
  });
  if (!enabled) return null;

  // The program-level switch only blocks interaction here - the workout value
  // stays as-is and saves unchanged, so flipping the program back on restores
  // the previous workout setting.
  const programSwapsOff = data?.collection.swapConfig?.swapsEnabled === false;

  const blocked = disabled || programSwapsOff || value === undefined;

  // Toggle's onClick fires regardless of its disabled styling - withholding
  // the handler is what actually blocks the interaction.
  const handleChange = blocked ? undefined : onChange;

  const toggle = (
    <div data-testid="workout-swaps-toggle">
      {/* Visual only: program-off shows the switch as off, but the form
          value is untouched and still saves whatever it was. */}
      <Toggle
        value={programSwapsOff ? false : !!value}
        variant="primary"
        disabled={blocked}
        onChange={handleChange}
      />
    </div>
  );

  return (
    <div className={styles.masterToggle}>
      <div className={styles.masterToggleRow}>
        <Typography
          variant="body"
          size="large"
          color={blocked ? 'N-60' : undefined}
        >
          Exercise modifications
        </Typography>
        {programSwapsOff ? (
          <Tooltip
            title="Exercise modifications are off for this program. Turn them on at the program level to enable this."
            variant="dark"
          >
            {toggle}
          </Tooltip>
        ) : (
          toggle
        )}
      </div>
      <Typography
        variant="body"
        size="medium"
        color={blocked ? 'N-60' : 'N-80'}
      >
        {value === undefined
          ? 'Exercise modifications are temporarily unavailable. Reload the page to try again.'
          : 'Allow AI-suggested swaps for this workout.'}
      </Typography>
    </div>
  );
};
